import { userSettingsStore } from "@/shared/stores/UserSettingsStore";
import { userClient } from "@/shared/rpc/clients";
import { useCallback, useEffect, useRef } from "react";

const SECTION = "trading";
const KEY = "demo-stats";
const WIN_COUNT_THRESHOLD = 5;

type DemoStatsValue = {
  winCount: number;
};

async function patchOrCreate(
  savedId: string | null,
  value: DemoStatsValue,
  idRef: React.MutableRefObject<string | null>,
): Promise<void> {
  try {
    if (savedId) {
      const result = await userClient.updateSetting({ id: savedId, section: SECTION, key: KEY, value });
      idRef.current = result.id;
      userSettingsStore.setSetting(result);
    } else {
      const result = await userClient.createSetting({ section: SECTION, key: KEY, value });
      idRef.current = result.id;
      userSettingsStore.setSetting(result);
    }
  } catch (error) {
    const code = (error as { code?: number })?.code;
    if (savedId && (code === 5 || code === 6)) {
      try {
        idRef.current = null;
        const result = await userClient.createSetting({ section: SECTION, key: KEY, value });
        idRef.current = result.id;
        userSettingsStore.setSetting(result);
      } catch {
        // silent
      }
    }
  }
}

export function useDemoStats() {
  const settingIdRef = useRef<string | null>(null);
  const winCountRef = useRef(0);

  useEffect(() => {
    userSettingsStore
      .load()
      .then(() => {
        const entry = userSettingsStore.getSetting(SECTION, KEY);
        if (entry) {
          settingIdRef.current = entry.id;
          const value = entry.value as DemoStatsValue | null;
          if (value) {
            winCountRef.current = value.winCount ?? 0;
          }
        }
      })
      .catch(() => {
        // silent
      });
  }, []);

  const onDemoWin = useCallback(async (): Promise<boolean> => {
    winCountRef.current += 1;
    const shouldShow = winCountRef.current >= WIN_COUNT_THRESHOLD;

    if (shouldShow) {
      winCountRef.current = 0;
    }

    await patchOrCreate(
      settingIdRef.current,
      { winCount: winCountRef.current },
      settingIdRef,
    );

    return shouldShow;
  }, []);

  return { onDemoWin };
}
